import type {
  CourseType,
  LevelUuid,
  UserProgress,
} from '@openeducation/oe-rn-shared-types';
import { styled } from '@openeducation/oe-rn-themes';
import React from 'react';
import { View } from 'react-native';

import type { DropdownItemType, CalendarProps } from '../../molecules';
import Calendar from '../../molecules/calendar/calendar';
import CalendarLegend from '../../molecules/calendar/calendar-legend';
import CourseSelector from '../../molecules/course-selector/course-selector';
import LevelProgressBar from '../../molecules/level-progress-bar/level-progress-bar';
import ProgressCircles from '../../molecules/progress-circles/progress-circles';
import { flex, height, mb, mr, mt, width } from '../../style-utils';

interface LevelProgressCalendarProps
  extends Omit<CalendarProps, 'selectedColor'> {
  courseType: CourseType | null;
  onCourseTypeChange: (courseType: CourseType | null) => void;
}

export interface LevelProgressProps {
  levelNameByUuid: {
    [key in LevelUuid]: string;
  };
  courseSelectorData: DropdownItemType[];
  progressProps: UserProgress;
  calendarProps: LevelProgressCalendarProps;
  labels: {
    ProgressCircles: {
      liveClasses: string;
      units: string;
      practices: string;
      practicesMaxValue: string;
      of: string;
    };
    CalendarLegend: {
      activityRegisteredLabel: string;
      todayLabel: string;
    };
  };
}

export const LevelProgress = ({
  levelNameByUuid,
  courseSelectorData,
  calendarProps: { courseType, onCourseTypeChange, ...calendarProps },
  labels,
  progressProps: {
    levelNumber,
    levelProgressPercent,
    liveClassProgressPercent,
    liveClassTaken,
    liveClassRequired,
    unitProgressPercent,
    unitTaken,
    unitRequired,
    practiceProgressPercent,
    practiceTaken,
    practiceRequired,
  },
}: LevelProgressProps) => {
  const circlesProps = {
    liveClassProgressPercent,
    liveClassTaken,
    liveClassRequired,
    unitProgressPercent,
    unitTaken,
    unitRequired,
    practiceProgressPercent,
    practiceTaken,
    practiceRequired,
  };

  return (
    <View style={[flex('flex-row')]}>
      <View style={[flex('flex-1'), mr(65)]}>
        <View style={[mt(30), mb(90)]}>
          <LevelProgressBar
            activeLevelNumber={levelNumber}
            activeLevelProgressPercent={levelProgressPercent}
            levelNameByUuid={levelNameByUuid}
          />
        </View>
        <ProgressCircles {...circlesProps} labels={labels.ProgressCircles} />
      </View>
      <StyledCalendar
        {...calendarProps}
        containerStyle={[width(340), height(440)]}
        headerRight={
          <CourseSelector
            value={courseType}
            onChange={onCourseTypeChange}
            data={courseSelectorData}
          />
        }
      >
        <CalendarLegend {...labels.CalendarLegend} />
      </StyledCalendar>
    </View>
  );
};

const StyledCalendar = styled(Calendar).attrs(({ theme }) => ({
  selectedColor: theme.color.olive,
}))``;

export default LevelProgress;
